/**
 * AppointmentResourcesAdmin.tsx — Admin > Appointment Resources.
 *
 * The corporate dashboard's Appointment Resources page for the signed-in
 * account (its resource rows, without the account grouping), in the Lead
 * Sources layout (see components/admin/AdminList.tsx):
 *   - Name, Type (corp's coloured pills; staff types in the account's own role
 *     titles), User, Appt Types (how many live appointment types book it by
 *     default) and Facilities (how many, listed on click; "All" when none);
 *   - corp's toolbar: search by name or user, Type and Facility filters (a
 *     resource with no facilities matches every facility), Show deleted;
 *   - "Add resource" top right; a click on a live row opens it; Edit / Delete
 *     on a live row, Restore on a deleted one;
 *   - corp's form: Title, Type, User (staff types; picking one offers to rename
 *     the resource to match), Description, Facilities ("Leave empty to make
 *     available at all facilities.") and, when editing, the linked appointment
 *     types.
 *
 * On a Locked page (useAdminPageLevel) nothing changes and nothing says why
 * (R8, R18): no Add, row icons or Actions column, and no wording that invites
 * a change; a resource still opens as a viewer, its fields read-only.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Building2, CalendarDays, Plus } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Input } from '@/components/ui/Input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/Popover';
import { SearchableCombobox } from '@/components/ui/SearchableCombobox';
import { SearchableMultiSelect } from '@/components/ui/SearchableMultiSelect';
import { Select } from '@/components/ui/Select';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import { Textarea } from '@/components/ui/Textarea';
import {
  ACTIONS_COLUMN,
  ADMIN_TD_CLASS,
  AdminListEmpty,
  AdminListPage,
  AdminListTable,
  AdminListToolbar,
  AdminRowName,
  PaginationFooter,
  adminRowClass,
  type AdminListColumn,
} from '@/components/admin/AdminList';
import { AdminFormModal } from '@/components/admin/AdminFormModal';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';
import { AdminRowAction, AdminRowActions } from '@/components/admin/AdminRowActions';
import { CountPill } from '@/components/admin/CorpBadges';
import { DESCRIPTION_CLASS, DialogField, fieldClasses } from '@/components/admin/DialogChrome';
import { ListFilterBar, anyFilterActive, type ListFilterConfig } from '@/components/admin/ListFilterBar';
import { useListPagination } from '@/components/admin/useListPagination';
import { compareText, useListSort, type ListSortCompare } from '@/components/admin/useListSort';
import { useTrackedForm } from '@/components/admin/useTrackedForm';
import { useRoleTitles } from '@/hooks/useRoleTitles';
import { buildStaffOptions, toIncludeIds } from '@/lib/staffOptions';
import { cn } from '@/lib/utils';
import { listFacilities } from '@/services/admin.service';
import { getErrorMessage } from '@/services/apiClient';
import { appointmentsService } from '@/services/appointments.service';
import {
  RESOURCE_TYPES,
  USER_LINKED_TYPES,
  createResource,
  deleteResource,
  listAdminResources,
  restoreResource,
  updateResource,
  type AdminResource,
  type ResourceType,
} from '@/services/appointmentResources.service';
import { listCoordinators, formatCoordinatorName } from '@/services/coordinators.service';
import { listDoctors, formatDoctorName } from '@/services/doctors.service';
import { listNurses, formatNurseName } from '@/services/nurses.service';
import { toast } from '@/store/toastStore';

/** Corp's type pill colours. */
const TYPE_CLASS: Record<ResourceType, string> = {
  doctors: 'bg-blue-50 text-blue-700 border-blue-200',
  coordinators: 'bg-violet-50 text-violet-700 border-violet-200',
  nurses: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  rooms: 'bg-amber-50 text-amber-700 border-amber-200',
  equipment: 'bg-cyan-50 text-cyan-700 border-cyan-200',
  other: 'bg-slate-100 text-slate-700 border-slate-200',
};

/** Corp's coloured type pill: in the list, and as the Type picker's options and value. */
function TypePill({ type, label }: { type: ResourceType; label: string }) {
  return (
    <span className={cn('inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium', TYPE_CLASS[type])}>{label}</span>
  );
}

const COLUMNS: AdminListColumn[] = [
  { key: 'name', header: 'Name', sortable: true },
  { key: 'type', header: 'Type', sortable: true, className: 'w-40' },
  { key: 'user', header: 'User', sortable: true },
  { key: 'apptTypes', header: 'Appt Types', className: 'w-28' },
  { key: 'facilities', header: 'Facilities', className: 'w-28' },
  ACTIONS_COLUMN,
];

/** A Locked page has no row actions, so no Actions column. */
const LOCKED_COLUMNS = COLUMNS.filter((c) => c !== ACTIONS_COLUMN);

interface LinkedType {
  appointmentTypeID: number;
  name: string;
}

interface Choice {
  value: number;
  label: string;
}

function resourceName(r: AdminResource): string {
  return r.name.trim() || `Resource #${r.resourceID}`;
}

function isUserType(type: ResourceType): boolean {
  return USER_LINKED_TYPES.includes(type);
}

function userLabel(r: AdminResource): string {
  if (r.userName) return r.userName;
  return isUserType(r.resourceType) && r.typeTableId > 0 ? `ID: ${r.typeTableId}` : '—';
}

/** Type labels, with the account's role titles for the staff types (corp's roleTitleFor). */
function useTypeLabels(): (type: ResourceType) => string {
  const { titles } = useRoleTitles();
  return useCallback(
    (type: ResourceType) => {
      const generic = RESOURCE_TYPES.find((t) => t.value === type)?.label ?? type;
      if (type === 'doctors') return titles.doctor ?? generic;
      if (type === 'nurses') return titles.nurse ?? generic;
      if (type === 'coordinators') return titles.coordinator ?? generic;
      return generic;
    },
    [titles],
  );
}

export function AppointmentResourcesAdmin() {
  const locked = useAdminPageLevel() === 'locked';
  const [resources, setResources] = useState<AdminResource[]>([]);
  const [linked, setLinked] = useState<Map<number, LinkedType[]>>(new Map());
  const [facilities, setFacilities] = useState<Choice[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [types, setTypes] = useState<string[]>([]);
  const [facilityFilter, setFacilityFilter] = useState<string[]>([]);
  const [showDeleted, setShowDeleted] = useState(false);
  const [editing, setEditing] = useState<AdminResource | 'new' | null>(null);
  const [deleting, setDeleting] = useState<AdminResource | null>(null);
  const typeLabel = useTypeLabels();

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      setResources(await listAdminResources());
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to load appointment resources'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    // Which live appointment types (outside deleted groups) book each resource by default.
    Promise.all([
      appointmentsService.listAllAppointmentTypesRaw({ includeDeleted: true }),
      appointmentsService.listAppointmentTypeGroups({ active: false }),
    ])
      .then(([apptTypes, deletedGroups]) => {
        const gone = new Set(deletedGroups.map((g) => g.appointmentTypeGroupID));
        const map = new Map<number, LinkedType[]>();
        for (const at of apptTypes) {
          if (at.active === false || (at.group_id != null && gone.has(at.group_id))) continue;
          for (const dr of at.default_resources ?? []) {
            const list = map.get(dr.resource_id) ?? [];
            if (!list.some((x) => x.appointmentTypeID === at.appointmentTypeID)) {
              list.push({ appointmentTypeID: at.appointmentTypeID, name: at.name });
            }
            map.set(dr.resource_id, list);
          }
        }
        map.forEach((list) => list.sort((a, b) => compareText(a.name, b.name)));
        setLinked(map);
      })
      .catch(() => setLinked(new Map()));
    listFacilities()
      .then((list) =>
        setFacilities(
          list
            .filter((f) => f.active)
            .map((f) => ({ value: f.facilityId, label: f.facilityName || `Facility #${f.facilityId}` }))
            .sort((a, b) => compareText(a.label, b.label)),
        ),
      )
      .catch(() => setFacilities([]));
  }, [load]);

  const facilityName = useCallback(
    (id: number) => facilities.find((f) => f.value === id)?.label ?? `Facility #${id}`,
    [facilities],
  );

  const filters: ListFilterConfig[] = [
    {
      id: 'resources-type-filter',
      label: 'Filter by type',
      placeholder: 'All types',
      options: RESOURCE_TYPES.map((t) => ({ value: t.value, label: typeLabel(t.value) })),
      selected: types,
      onChange: setTypes,
    },
    {
      id: 'resources-facility-filter',
      label: 'Filter by facility',
      placeholder: 'All facilities',
      searchPlaceholder: 'Search facilities…',
      emptyMessage: 'No facilities found.',
      options: facilities.map((f) => ({ value: String(f.value), label: f.label })),
      selected: facilityFilter,
      onChange: setFacilityFilter,
    },
  ];
  const filtering = anyFilterActive(filters);

  const query = search.trim().toLowerCase();
  const rows = useMemo(() => {
    const typeSet = new Set(types);
    const facSet = new Set(facilityFilter.map(Number));
    return resources
      .filter((r) => showDeleted || r.active)
      .filter((r) => typeSet.size === 0 || typeSet.has(r.resourceType))
      // No facilities = every facility.
      .filter((r) => facSet.size === 0 || r.facilityIds.length === 0 || r.facilityIds.some((id) => facSet.has(id)))
      .filter((r) => query === '' || r.name.toLowerCase().includes(query) || r.userName.toLowerCase().includes(query))
      .sort((a, b) => compareText(resourceName(a), resourceName(b)) || a.resourceID - b.resourceID);
  }, [resources, showDeleted, types, facilityFilter, query]);

  const sortCompare: ListSortCompare<AdminResource> = useMemo(
    () => ({
      name: (a, b) => compareText(resourceName(a), resourceName(b)),
      type: (a, b) => compareText(typeLabel(a.resourceType), typeLabel(b.resourceType)),
      user: (a, b) => compareText(userLabel(a), userLabel(b)),
    }),
    [typeLabel],
  );
  const sorting = useListSort(rows, { initialKey: 'name', compare: sortCompare });
  const pagination = useListPagination(sorting.sorted, { resetOn: [query, showDeleted, types, facilityFilter] });

  async function handleDelete() {
    if (!deleting) return;
    const { resourceID } = deleting;
    try {
      await deleteResource(resourceID);
      setResources((prev) => prev.map((r) => (r.resourceID === resourceID ? { ...r, active: false } : r)));
      toast.success('Resource deleted');
    } catch (err) {
      toast.error(getErrorMessage(err, 'The resource could not be deleted.'));
    }
  }

  async function handleRestore(r: AdminResource) {
    try {
      await restoreResource(r.resourceID);
      setResources((prev) => prev.map((x) => (x.resourceID === r.resourceID ? { ...x, active: true } : x)));
      toast.success('Resource restored');
    } catch (err) {
      toast.error(getErrorMessage(err, 'The resource could not be restored.'));
    }
  }

  const narrowed = query !== '' || filtering;

  return (
    <AdminListPage
      title="Appointment Resources"
      subtitle={
        locked ? 'Appointment resources such as doctors, rooms, and equipment' : 'Manage appointment resources such as doctors, rooms, and equipment'
      }
      actions={
        <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setEditing('new')}>
          Add resource
        </Button>
      }
      toolbar={
        <AdminListToolbar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search by name or user..."
          searchLabel="Search resources"
          showDeleted={showDeleted}
          onShowDeletedChange={setShowDeleted}
          showDeletedId="resources-show-deleted"
        >
          <ListFilterBar filters={filters} />
        </AdminListToolbar>
      }
    >
      {loading ? (
        <SpinnerOverlay label="Loading appointment resources…" />
      ) : rows.length === 0 ? (
        <AdminListEmpty
          title={narrowed ? 'No resources found' : 'No resources yet'}
          hint={narrowed ? 'Try adjusting your search or filters.' : locked ? undefined : 'Add a resource to get started.'}
        />
      ) : (
        <>
          <AdminListTable columns={locked ? LOCKED_COLUMNS : COLUMNS} aria-label="Appointment resources" sort={sorting.sort} onSortChange={sorting.onSortChange}>
            {pagination.pageItems.map((r) => {
              const deleted = !r.active;
              const name = resourceName(r);
              const apptTypes = linked.get(r.resourceID) ?? [];
              return (
                <tr
                  key={r.resourceID}
                  className={adminRowClass({ deleted, clickable: !deleted })}
                  onClick={deleted ? undefined : () => setEditing(r)}
                >
                  <td className={ADMIN_TD_CLASS}>
                    <AdminRowName deleted={deleted} nameTestId="resource-name">
                      {name}
                    </AdminRowName>
                  </td>
                  <td className={ADMIN_TD_CLASS}>
                    <TypePill type={r.resourceType} label={typeLabel(r.resourceType)} />
                  </td>
                  <td className={cn(ADMIN_TD_CLASS, 'text-slate-600')}>{userLabel(r)}</td>
                  <td className={ADMIN_TD_CLASS}>
                    {apptTypes.length > 0 ? (
                      <CountPill tone="indigo" icon={<CalendarDays />} count={apptTypes.length} title="Appointment types" />
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  <td className={ADMIN_TD_CLASS}>
                    {r.facilityIds.length === 0 ? (
                      <span className="italic text-slate-400">All</span>
                    ) : (
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            aria-label={`Facilities for ${name}`}
                            onClick={(e) => e.stopPropagation()}
                            className="rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                          >
                            <CountPill tone="teal" icon={<Building2 />} count={r.facilityIds.length} />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-56 p-3" onClick={(e) => e.stopPropagation()}>
                          <p className="mb-2 text-xs font-semibold text-slate-600">Facilities</p>
                          <ul className="space-y-1 text-sm text-slate-700">
                            {r.facilityIds.map((id) => (
                              <li key={id}>{facilityName(id)}</li>
                            ))}
                          </ul>
                        </PopoverContent>
                      </Popover>
                    )}
                  </td>
                  {!locked && (
                    <td className={ADMIN_TD_CLASS}>
                      <AdminRowActions>
                        {deleted ? (
                          <AdminRowAction kind="restore" label={`Restore ${name}`} onClick={() => void handleRestore(r)} />
                        ) : (
                          <>
                            <AdminRowAction kind="edit" label={`Edit ${name}`} onClick={() => setEditing(r)} />
                            <AdminRowAction kind="delete" label={`Delete ${name}`} onClick={() => setDeleting(r)} />
                          </>
                        )}
                      </AdminRowActions>
                    </td>
                  )}
                </tr>
              );
            })}
          </AdminListTable>
          <PaginationFooter pagination={pagination} noun={['resource', 'resources']} />
        </>
      )}

      {editing && (
        <ResourceFormModal
          key={editing === 'new' ? 'new' : editing.resourceID}
          resource={editing === 'new' ? null : editing}
          linkedTypes={editing === 'new' ? [] : linked.get(editing.resourceID) ?? []}
          facilities={facilities}
          typeLabel={typeLabel}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load(true);
          }}
        />
      )}

      <ConfirmDialog
        isOpen={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
        title="Delete resource?"
        message={deleting ? `${resourceName(deleting)} will no longer be bookable. You can restore it from Show deleted.` : ''}
        confirmLabel="Delete resource"
        variant="danger"
      />
    </AdminListPage>
  );
}

function ResourceFormModal({
  resource,
  linkedTypes,
  facilities,
  typeLabel,
  onClose,
  onSaved,
}: {
  resource: AdminResource | null;
  linkedTypes: LinkedType[];
  facilities: Choice[];
  typeLabel: (type: ResourceType) => string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isNew = resource === null;
  const locked = useAdminPageLevel() === 'locked';
  const t = useTrackedForm(
    () => ({
      name: resource?.name ?? '',
      resourceType: resource?.resourceType ?? ('doctors' as ResourceType),
      typeTableId: resource?.typeTableId ?? 0,
      description: resource?.description ?? '',
      facilityIds: resource?.facilityIds ?? ([] as number[]),
    }),
    { tracking: !isNew },
  );
  const [saving, setSaving] = useState(false);
  const [staff, setStaff] = useState<Record<'doctors' | 'nurses' | 'coordinators', Choice[]>>({ doctors: [], nurses: [], coordinators: [] });
  const [renameTo, setRenameTo] = useState<string | null>(null);

  // Each staff list keeps this resource's own person even if they have since left.
  useEffect(() => {
    const keep = (type: ResourceType) => (resource?.resourceType === type ? toIncludeIds(resource.typeTableId) : []);
    let cancelled = false;
    Promise.all([
      listDoctors({ includeIds: keep('doctors') }),
      listNurses({ includeIds: keep('nurses') }),
      listCoordinators({ includeIds: keep('coordinators') }),
    ])
      .then(([d, n, c]) => {
        if (cancelled) return;
        setStaff({
          doctors: buildStaffOptions(d, (r) => r.doctorID, formatDoctorName),
          nurses: buildStaffOptions(n, (r) => r.nurseID, formatNurseName),
          coordinators: buildStaffOptions(c, (r) => r.coordID, formatCoordinatorName),
        });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [resource]);

  const userType = isUserType(t.form.resourceType);
  const users = userType ? staff[t.form.resourceType as 'doctors' | 'nurses' | 'coordinators'] : [];
  const missingName = t.form.name.trim() === '';
  const missingUser = userType && t.form.typeTableId <= 0;
  const errors = [...(missingName ? ['Enter a resource title'] : []), ...(missingUser ? ['Select a user'] : [])];

  function pickUser(id: number) {
    t.patch({ typeTableId: id });
    const person = users.find((u) => u.value === id);
    // Corp offers to rename the resource after the person picked.
    if (person && id > 0 && person.label.replace(/ \(inactive\)$/, '') !== t.form.name) {
      setRenameTo(person.label.replace(/ \(inactive\)$/, ''));
    }
  }

  async function save() {
    if (errors.length > 0) return;
    setSaving(true);
    try {
      const data = {
        name: t.form.name.trim(),
        resource_type: t.form.resourceType,
        description: t.form.description,
        type_table_id: userType ? t.form.typeTableId : 0,
        facility_ids: t.form.facilityIds,
      };
      if (resource) await updateResource(resource.resourceID, data);
      else await createResource(data);
      toast.success(isNew ? 'Resource created' : 'Resource updated');
      onSaved();
    } catch (err) {
      toast.error(getErrorMessage(err, 'The resource could not be saved.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <AdminFormModal
      title={isNew ? 'Add Resource' : 'Edit Resource'}
      description={locked ? undefined : isNew ? 'Create a new appointment resource.' : 'Update the resource details.'}
      isNew={isNew}
      dirty={isNew ? t.form.name !== '' || t.form.description !== '' || t.form.facilityIds.length > 0 || t.form.typeTableId > 0 : t.dirty}
      saving={saving}
      errors={errors}
      cancelLabel="Close"
      onSave={() => void save()}
      onRevertAll={t.revertAll}
      onClose={onClose}
      // Corp's dialog width.
      panelClassName="md:max-w-[560px]"
    >
      <div className="grid gap-4">
        <DialogField
          label="Title"
          htmlFor="resource-name"
          state={missingName ? 'error' : 'none'}
          labelHint={missingName ? '— required' : undefined}
          badge={t.badge('name', 'Title')}
        >
          <Input
            id="resource-name"
            value={t.form.name}
            placeholder="Resource name"
            maxLength={255}
            onChange={(e) => t.patch({ name: e.target.value })}
            className={fieldClasses.input(missingName ? 'error' : 'none')}
          />
        </DialogField>
        <DialogField label="Type" htmlFor="resource-type" badge={t.badge('resourceType', 'Type')}>
          {/* Corp's picker: the coloured type pills are the options, and the closed field shows the chosen one. */}
          <Select<ResourceType>
            id="resource-type"
            options={RESOURCE_TYPES.map((rt) => ({ value: rt.value, label: typeLabel(rt.value) }))}
            value={t.form.resourceType}
            onChange={(v) => v && t.patch({ resourceType: v, typeTableId: 0 })}
            renderOption={(o) => <TypePill type={o.value} label={o.label} />}
            renderValue={(o) => <TypePill type={o.value} label={o.label} />}
            wrapperClassName="w-fit"
          />
        </DialogField>
        {userType && (
          <DialogField
            label="User"
            htmlFor="resource-user"
            state={missingUser ? 'error' : 'none'}
            labelHint={missingUser ? '— required' : undefined}
            badge={t.badge('typeTableId', 'User')}
          >
            <SearchableCombobox<number>
              id="resource-user"
              options={[{ value: 0, label: 'None' }, ...users]}
              value={t.form.typeTableId}
              onChange={(v) => pickUser(v ?? 0)}
              placeholder="Select user"
            />
          </DialogField>
        )}
        <DialogField label="Description" htmlFor="resource-description" badge={t.badge('description', 'Description')}>
          <Textarea autoResize
            id="resource-description"
            rows={4}
            value={t.form.description}
            placeholder="Optional description"
            onChange={(e) => t.patch({ description: e.target.value })}
          />
        </DialogField>
        <DialogField
          label="Facilities"
          htmlFor="resource-facilities"
          badge={t.badge('facilityIds', 'Facilities')}
          description={locked ? undefined : 'Leave empty to make available at all facilities.'}
        >
          <SearchableMultiSelect
            id="resource-facilities"
            options={facilities.map((f) => ({ value: String(f.value), label: f.label }))}
            selectedValues={t.form.facilityIds.map(String)}
            onSelectionChange={(v) => t.patch({ facilityIds: v.map(Number) })}
            placeholder="All facilities"
            searchPlaceholder="Search facilities…"
            emptyMessage="No facilities found."
          />
        </DialogField>
        {!isNew && (
          // Corp's block: under a rule, each type a small card with a calendar icon.
          <div className="mt-1 border-t border-slate-200 pt-3">
            <p className="text-xs font-medium uppercase text-slate-500">Linked Appointment Types</p>
            {linkedTypes.length === 0 ? (
              <p className={cn(DESCRIPTION_CLASS, 'py-1 text-sm')}>No appointment types use this resource.</p>
            ) : (
              <ul className="mt-1 space-y-1" data-testid="linked-appointment-types">
                {linkedTypes.map((at) => (
                  <li
                    key={at.appointmentTypeID}
                    className="flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700"
                  >
                    <CalendarDays className="h-3.5 w-3.5 shrink-0 text-slate-400" aria-hidden />
                    <span className="truncate">{at.name}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      <ConfirmDialog
        isOpen={renameTo !== null}
        onClose={() => setRenameTo(null)}
        onConfirm={() => {
          if (renameTo) t.patch({ name: renameTo });
          setRenameTo(null);
        }}
        title="Change resource name to match user?"
        message={renameTo ? `Rename this resource to "${renameTo}"?` : ''}
        confirmLabel="Change name"
        cancelLabel="Keep name"
        variant="warning"
      />
    </AdminFormModal>
  );
}
